import React from "react";
import { useTranslation } from "react-i18next";
import styles from "./VisitorsCard.module.scss";
import { useFetchVisitorsQuery } from "../../../api/transactionsApi";
import { tokens } from "../../../styles/tokens";

interface DailyPoint {
  day: string;
  visitors: number;
}

const Sparkline: React.FC<{ series: DailyPoint[] }> = ({ series }) => {
  if (!series || series.length === 0) {
    return null;
  }
  const width = 260;
  const height = 56;
  const pad = 4;
  const max = Math.max(1, ...series.map((p) => p.visitors));
  const step = series.length > 1 ? (width - pad * 2) / (series.length - 1) : 0;

  const points = series.map((p, i) => {
    const x = pad + i * step;
    const y = pad + (height - pad * 2) * (1 - p.visitors / max);
    return { x, y };
  });

  const linePath = points
    .map((pt, i) => `${i === 0 ? "M" : "L"}${pt.x.toFixed(1)},${pt.y.toFixed(1)}`)
    .join(" ");

  const areaPath =
    `M${points[0].x.toFixed(1)},${(height - pad).toFixed(1)} ` +
    points
      .map((pt) => `L${pt.x.toFixed(1)},${pt.y.toFixed(1)}`)
      .join(" ") +
    ` L${points[points.length - 1].x.toFixed(1)},${(height - pad).toFixed(1)} Z`;

  return (
    <svg
      className={styles.sparkline}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="visitors-spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={tokens.indigo600} stopOpacity="0.45" />
          <stop offset="100%" stopColor={tokens.indigo600} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#visitors-spark-fill)" />
      <path
        d={linePath}
        fill="none"
        stroke={tokens.indigo600}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const formatStat = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat().format(value);
};

const VisitorsCard: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useFetchVisitorsQuery();

  const hasError =
    isError ||
    !!data?.error ||
    (!!data &&
      data.today === null &&
      data.thisWeek === null &&
      data.thisMonth === null);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t("visitorsCard.title", "Visitors")}</h2>
        <span className={styles.badge}>
          {t("visitorsCard.live", "Live")}
        </span>
      </div>

      {isLoading ? (
        <div className={styles.statsRow}>
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.stat}>
              <div className={`${styles.skeleton} ${styles.skeletonValue}`} />
              <div className={`${styles.skeleton} ${styles.skeletonLabel}`} />
            </div>
          ))}
        </div>
      ) : hasError ? (
        <div className={styles.unavailable}>
          {t(
            "visitorsCard.unavailable",
            "Analytics temporarily unavailable"
          )}
        </div>
      ) : (
        <>
          <div className={styles.statsRow}>
            <div className={styles.stat}>
              <div className={styles.value}>{formatStat(data?.today)}</div>
              <div className={styles.label}>
                {t("visitorsCard.today", "Today")}
              </div>
            </div>
            <div className={styles.stat}>
              <div className={styles.value}>{formatStat(data?.thisWeek)}</div>
              <div className={styles.label}>
                {t("visitorsCard.thisWeek", "This Week")}
              </div>
            </div>
            <div className={styles.stat}>
              <div className={styles.value}>{formatStat(data?.thisMonth)}</div>
              <div className={styles.label}>
                {t("visitorsCard.thisMonth", "This Month")}
              </div>
            </div>
          </div>
          <Sparkline series={data?.dailySeries ?? []} />
        </>
      )}
    </div>
  );
};

export default VisitorsCard;
